import { Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { useEffect, useMemo, useState } from 'react';
import { demeterPageElementsApi } from '../../../../Apis/Apis';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import { IChartProps } from '../ChartDefinitions';

export interface IImageChartRawProps extends IChartProps {
    imageSource: string;
}

const ImageChartRaw: React.FC<IImageChartRawProps> = (props: IImageChartRawProps) => {
    const [ratio, setRatio] = useState<number>(1);
    const [embeddedSource, setEmbeddedSource] = useState<string>('');

    const [, getDemeterPageElementEmbeddedImage, getDemeterPageElementEmbeddedImageResponse] = useApiWithoutAutoExecute(() =>
        demeterPageElementsApi.getDemeterPageElementEmbeddedImage(props.imageSource),
    );

    useEffect(() => {
        getDemeterPageElementEmbeddedImage();
    }, [props.imageSource]);

    useEffect(() => {
        if (!getDemeterPageElementEmbeddedImageResponse) {
            return;
        }
        const dataUrl = `data:${getDemeterPageElementEmbeddedImageResponse.contentType || 'image/png'};base64,${
            getDemeterPageElementEmbeddedImageResponse.base64
        }`;

        setEmbeddedSource(dataUrl);
    }, [getDemeterPageElementEmbeddedImageResponse]);

    useEffect(() => {
        if (!embeddedSource) {
            setRatio(1);
            return;
        }

        const img = new Image();
        img.onload = () => {
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;

            if (w > 0 && h > 0) {
                setRatio(w / h);
            } else {
                setRatio(1);
            }
        };
        img.src = embeddedSource;
    }, [embeddedSource]);

    const options: Options = useMemo(
        () => ({
            chart: {
                backgroundColor: 'transparent',
                plotBackgroundColor: 'transparent',
                margin: 0,
                animation: false,
                events: {
                    load() {
                        const chart = this as any;
                        const { renderer, plotWidth, plotHeight, plotLeft, plotTop } = chart;

                        const footerReserve = 60;
                        const topOffset = 70;

                        const imgHeight = Math.max(plotHeight - footerReserve - topOffset, 0);
                        const imgWidth = imgHeight * ratio;

                        const x = plotLeft + (plotWidth - imgWidth) / 2;
                        const y = plotTop + topOffset;

                        if (chart.customBackgroundImage) {
                            chart.customBackgroundImage.destroy();
                        }

                        if (embeddedSource) {
                            chart.customBackgroundImage = renderer.image(embeddedSource, x, y, imgWidth, imgHeight).add();
                        }
                    },
                    redraw() {
                        const chart = this as any;
                        const { renderer, plotWidth, plotHeight, plotLeft, plotTop } = chart;

                        const footerReserve = 60;
                        const topOffset = 70;

                        const imgHeight = Math.max(plotHeight - footerReserve - topOffset, 0);
                        const imgWidth = imgHeight * ratio;

                        const x = plotLeft + (plotWidth - imgWidth) / 2;
                        const y = plotTop + topOffset;

                        if (chart.customBackgroundImage) {
                            chart.customBackgroundImage.destroy();
                        }

                        if (embeddedSource) {
                            chart.customBackgroundImage = renderer.image(embeddedSource, x, y, imgWidth, imgHeight).add();
                        }
                    },
                },
            },
            title: { text: '' },
            credits: { enabled: false },
            xAxis: { visible: false },
            yAxis: { visible: false },
            series: [],
            exporting: {},
        }),
        [embeddedSource, ratio],
    );

    return (
        <HighchartsReact ref={props.chartReference} highcharts={HighStock} options={options} containerProps={{ style: { width: '100%', height: '100%' } }} />
    );
};

export default ImageChartRaw;
